import { useEffect, useState } from 'react'
import { CHECK_CATEGORIES, CHECK_OUTCOMES, countOutcomes } from '@qim/shared'
import { ApiError, fetchCheckRuns, runChecks } from '../api.js'
import { formatInstant } from '../format.js'
import type { JSX } from 'react'
import type { CheckCategory, CheckOutcome, CheckResult, CheckRun } from '@qim/shared'

/**
 * An instance's Debug tab: the standard checks, and the runs kept (IM-199).
 *
 * ## The API decides, this renders
 *
 * Every outcome is `runChecks` in `shared`, run by the API over facts it read.
 * Nothing here judges a result — the same rule the onboarding screen follows,
 * so the tab and the API cannot disagree about whether something is wrong.
 *
 * ## Reading the tab reads nothing from the customer's account
 *
 * Opening it lists the runs already kept, which is one read of Qrvey's own
 * table. Only "Run checks" reaches into the account, so it happens on a click
 * and lands in the audit trail with the person who asked.
 *
 * ## Word and shape, then colour
 *
 * An outcome is a glyph and a word, coloured third — the deployment status
 * rule, for the same projector and the same screenshot in a ticket.
 */

const OUTCOME_SHAPES: Readonly<Record<CheckOutcome, { glyph: string; label: string }>> = {
  fail: { glyph: '▲', label: 'Fail' },
  warn: { glyph: '△', label: 'Warning' },
  unknown: { glyph: '?', label: 'Unknown' },
  ok: { glyph: '●', label: 'OK' },
  'not-applicable': { glyph: '○', label: 'Not applicable' },
}

const CATEGORY_LABELS: Readonly<Record<CheckCategory, string>> = {
  capacity: 'Capacity',
  network: 'Network',
  'managed-services': 'Managed services',
  cost: 'Cost',
  cluster: 'Cluster',
  observability: 'Observability',
  'customer-stacks': 'Customer stacks',
}

const RANK = new Map(CHECK_OUTCOMES.map((outcome, index) => [outcome, index]))

export const CheckOutcomeBadge = ({ outcome }: { readonly outcome: CheckOutcome }): JSX.Element => {
  const shape = OUTCOME_SHAPES[outcome]
  return (
    <span className={`check-outcome check-outcome-${outcome}`}>
      <span aria-hidden="true">{shape.glyph}</span> {shape.label}
    </span>
  )
}

/** "2 fail · 1 warning · 3 OK", leaving out what did not happen. */
const summarize = (results: readonly CheckResult[]): string => {
  const counts = countOutcomes(results)
  return CHECK_OUTCOMES.filter((o) => counts[o] > 0)
    .map((o) => `${String(counts[o])} ${OUTCOME_SHAPES[o].label.toLowerCase()}`)
    .join(' · ')
}

const Result = ({ result }: { readonly result: CheckResult }): JSX.Element => (
  <li className="check-result">
    <p>
      <CheckOutcomeBadge outcome={result.outcome} /> <strong>{result.title}</strong>
    </p>
    <p>{result.finding}</p>
    {result.recommendation === undefined ? null : <p className="muted">{result.recommendation}</p>}
    {result.evidence.length === 0 ? null : (
      <dl className="facts">
        {result.evidence.map((e, index) => (
          <div key={`${e.label}-${String(index)}`} className="check-evidence">
            <dt>{e.label}</dt>
            <dd>{e.value}</dd>
          </div>
        ))}
      </dl>
    )}
  </li>
)

const RunView = ({ run }: { readonly run: CheckRun }): JSX.Element => {
  if (run.state === 'running' || run.results === undefined) {
    return (
      <p className="failure">
        This run started <time dateTime={run.requestedAt}>{formatInstant(run.requestedAt)}</time>{' '}
        and never finished, so it has no results. Run the checks again.
      </p>
    )
  }
  const results = run.results
  return (
    <>
      <p className="muted">
        Checked <time dateTime={run.requestedAt}>{formatInstant(run.requestedAt)}</time> by{' '}
        {run.requestedBy}, in {run.region}. {summarize(results)}.
      </p>
      {CHECK_CATEGORIES.map((category) => {
        const inCategory = results
          .filter((r) => r.category === category)
          .sort((a, b) => (RANK.get(a.outcome) ?? 0) - (RANK.get(b.outcome) ?? 0))
        return inCategory.length === 0 ? null : (
          <section key={category} aria-label={CATEGORY_LABELS[category]}>
            <h3>{CATEGORY_LABELS[category]}</h3>
            <ul className="check-list">
              {inCategory.map((result) => (
                <Result key={result.id} result={result} />
              ))}
            </ul>
          </section>
        )
      })}
    </>
  )
}

export const Debug = ({
  baseUrl,
  tenantId,
  accountKey,
  deploymentId,
}: {
  readonly baseUrl: string
  readonly tenantId: string
  readonly accountKey: string
  readonly deploymentId: string
}): JSX.Element => {
  const [runs, setRuns] = useState<readonly CheckRun[] | undefined>(undefined)
  const [selected, setSelected] = useState<string | undefined>(undefined)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | undefined>(undefined)

  useEffect(() => {
    let live = true
    fetchCheckRuns(baseUrl, tenantId, accountKey, deploymentId).then(
      (kept) => {
        if (live) setRuns(kept)
      },
      () => {
        if (!live) return
        setRuns([])
        setProblem('The checks kept for this instance could not be read.')
      },
    )
    return () => {
      live = false
    }
  }, [baseUrl, tenantId, accountKey, deploymentId])

  const onRun = (): void => {
    setBusy(true)
    setProblem(undefined)
    runChecks(baseUrl, tenantId, accountKey, deploymentId).then(
      (run) => {
        setRuns((kept) => [run, ...(kept ?? [])])
        setSelected(run.runId)
        setBusy(false)
      },
      (error: unknown) => {
        setProblem(
          error instanceof ApiError && error.status === 404
            ? 'This instance could not be found.'
            : 'The checks could not be run. Try again in a moment.',
        )
        setBusy(false)
      },
    )
  }

  const shown = runs?.find((r) => r.runId === selected) ?? runs?.[0]

  return (
    <section className="panel">
      <h2>Checks</h2>
      <p className="muted">
        Reads this instance’s account through the read-only diagnostics role and reports what looks
        wrong. Nothing is changed.
      </p>
      <div className="actions">
        <button type="button" className="primary" onClick={onRun} disabled={busy}>
          {busy ? 'Running checks…' : 'Run checks'}
        </button>
      </div>
      {problem === undefined ? null : (
        <p className="failure" role="alert">
          {problem}
        </p>
      )}

      {runs === undefined ? (
        <p className="muted">Loading…</p>
      ) : shown === undefined ? (
        <p className="muted">No checks have been run against this instance yet.</p>
      ) : (
        <RunView run={shown} />
      )}

      {runs !== undefined && runs.length > 1 ? (
        <details>
          <summary>Earlier runs ({String(runs.length - 1)})</summary>
          <ul className="check-runs">
            {runs.map((run) => (
              <li key={run.runId}>
                <button
                  type="button"
                  aria-current={run.runId === shown?.runId ? 'true' : undefined}
                  onClick={() => setSelected(run.runId)}
                >
                  <time dateTime={run.requestedAt}>{formatInstant(run.requestedAt)}</time>
                </button>{' '}
                <span className="muted">
                  {run.requestedBy}
                  {run.results === undefined ? ' · did not finish' : ` · ${summarize(run.results)}`}
                </span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </section>
  )
}
